/**
 * Root-level chat-mode signal (prepared 2026-07-13; LIVE app-wide since the
 * 2026-08-24 owner directive).
 *
 * When the chat is OPEN in DOCKED/side mode it occupies a right-hand rail.
 * While that is true, `data-prest0-chat-mode="docked"` is stamped on <html>
 * (document.documentElement); it is removed the moment the pane closes,
 * floats, goes fullscreen, or is hidden. The app-wide content reflow keys
 * on it (RoutedContentShell in App.tsx):
 *
 *   html[data-prest0-chat-mode="docked"] & { width: calc(100% - rail); }
 *
 * so every routed page lays out in the remaining space on the left — like a
 * narrower browser window — exactly while the side panel is visible. The
 * attribute means "the docked pane is on screen right now", not "docked is
 * the stored preference": the caller (GlobalChatSystem) includes isChatOpen
 * and !config.hidden in the condition, so a closed-but-docked pill never
 * squeezes the page. Keep any new CSS consumers on that same meaning.
 */
import { useEffect } from 'react';

export const CHAT_MODE_ATTRIBUTE = 'data-prest0-chat-mode';
export const CHAT_MODE_DOCKED = 'docked';

/**
 * Pure applier — exported for tests and for any non-React caller. Sets the
 * attribute when `isDocked` is true, removes it otherwise. SSR-safe: no-ops
 * without a document.
 */
export function applyChatModeAttribute(
  isDocked: boolean,
  doc: Document | undefined = typeof document !== 'undefined' ? document : undefined,
): void {
  const rootElement = doc?.documentElement;
  if (!rootElement) return;
  if (isDocked) {
    rootElement.setAttribute(CHAT_MODE_ATTRIBUTE, CHAT_MODE_DOCKED);
  } else {
    rootElement.removeAttribute(CHAT_MODE_ATTRIBUTE);
  }
}

/**
 * Keeps document.documentElement's chat-mode attribute in sync with the
 * mode state that GlobalChatSystem (the single persistent chat mount) owns.
 * Cleans the attribute up on unmount so a torn-down chat never leaves a
 * stale "docked" stamp behind.
 */
export function useChatModeDocumentSignal(isDocked: boolean): void {
  useEffect(() => {
    applyChatModeAttribute(isDocked);
    return () => applyChatModeAttribute(false);
  }, [isDocked]);
}
